import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Landmark,
  TrendingUp,
  LineChart,
  PiggyBank,
  Mail,
  Phone,
} from "lucide-react";
import Header from "../components/Header";
import ContactFooter from "../components/ContactFooter";
import { Reveal } from "../hooks/useReveal";
import { SUPPORT_EMAIL } from "../data/siteContent";

const SUPPORT_PHONE = "+1 (954) 598-3681";

const financialServices = [
  {
    icon: Landmark,
    title: "Trust & Asset Protection Setup",
    lines: ["Living Trust (Basic): $1,497", "Living Trust (Private / Enhanced): $2,497"],
    text: "Includes document preparation, review, and guidance on proper funding of your trust.",
  },
  {
    icon: TrendingUp,
    title: "Financial Consulting Sessions",
    lines: ["One-on-One Advisory: $500 per session", "Intensive Strategy Day: $1,000"],
    text: "Topics: investment strategy, portfolio allocation, income planning, wealth preservation.",
  },
  {
    icon: LineChart,
    title: "Ongoing Portfolio Management",
    lines: ["Daily advisory and monitoring rate: $500\u2013$1,000"],
    text: "Clients on active management programs receive daily oversight, strategy updates, and direct access to their advisor. Custom pricing available based on portfolio size and complexity.",
  },
  {
    icon: PiggyBank,
    title: "Retirement & Income Planning",
    lines: ["Consultation-based pricing starting at $500"],
    text: "Transition planning for professionals approaching retirement. Income stream structuring, residual income strategy, and long-term wealth allocation guidance.",
  },
];

const howItWorksSteps = [
  "Schedule your initial consultation",
  "We review your current financial position and goals",
  "We build a customized management plan",
  "Ongoing advisory, oversight, and reporting",
];

const FinancialConsulting = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-parchment min-h-screen antialiased" data-testid="financial-consulting-page">
      <Header light />

      <article className="pt-36 sm:pt-44 pb-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-goldeep text-sm font-medium tracking-wide hover:text-ink transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>

          <Reveal>
            <div className="flex items-center gap-3 text-[11px] sm:text-xs tracking-[0.3em] uppercase font-semibold text-gold mt-8">
              <span className="h-px w-10 bg-gold/60" />
              Financial Consulting
            </div>
            <h1
              className="font-display text-3xl sm:text-5xl text-ink tracking-tight leading-[1.08] mt-5"
              data-testid="financial-consulting-headline"
            >
              Strategic Financial Consulting{" "}
              <em className="text-goldeep italic">& Portfolio Management</em>
            </h1>
            <p className="text-ink/70 text-base sm:text-lg leading-relaxed mt-6 max-w-3xl">
              Personalized financial guidance and active management services for individuals
              seeking to protect and grow their wealth.
            </p>
            <Link
              to="/contact"
              data-testid="financial-consulting-hero-cta"
              className="group inline-flex items-center gap-2 bg-gold text-ink font-bold tracking-[0.08em] uppercase text-sm px-7 py-4 rounded-full hover:bg-goldlight transition-colors duration-300 mt-8"
            >
              Schedule a Consultation
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>

          <div className="mt-20">
            <Reveal>
              <h2 className="font-display text-2xl sm:text-3xl text-ink tracking-tight">
                Our Financial Services
              </h2>
            </Reveal>
            <div className="grid sm:grid-cols-2 gap-5 sm:gap-6 mt-8">
              {financialServices.map((service, idx) => {
                const Icon = service.icon;
                return (
                  <Reveal key={service.title} delay={idx * 70} className="h-full">
                    <div
                      className="h-full bg-white border border-ink/10 rounded-2xl p-7 shadow-sm"
                      data-testid={`financial-service-card-${idx}`}
                    >
                      <div className="w-11 h-11 rounded-full bg-goldeep/10 border border-goldeep/25 flex items-center justify-center text-goldeep">
                        <Icon className="w-5 h-5" strokeWidth={1.8} />
                      </div>
                      <h3 className="font-display text-lg text-ink mt-5">{service.title}</h3>
                      <ul className="mt-4 space-y-1.5">
                        {service.lines.map((line) => (
                          <li
                            key={line}
                            className="text-goldeep font-semibold text-sm tracking-wide"
                          >
                            {line}
                          </li>
                        ))}
                      </ul>
                      <p className="text-ink/60 text-sm leading-relaxed mt-4">{service.text}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>

          <div className="mt-20">
            <Reveal>
              <h2 className="font-display text-2xl sm:text-3xl text-ink tracking-tight">
                How It Works
              </h2>
              <ol className="mt-8 space-y-5">
                {howItWorksSteps.map((step, idx) => (
                  <li key={step} className="flex items-start gap-4" data-testid={`how-it-works-step-${idx}`}>
                    <span className="flex-shrink-0 w-9 h-9 rounded-full bg-ink text-cream font-display text-sm flex items-center justify-center">
                      {idx + 1}
                    </span>
                    <p className="text-ink/75 text-base leading-relaxed mt-1">{step}</p>
                  </li>
                ))}
              </ol>
            </Reveal>
          </div>

          <div className="mt-20">
            <Reveal>
              <h2 className="font-display text-xl text-ink tracking-tight">
                Refund & Cancellation Policy
              </h2>
              <p
                className="text-ink/60 text-sm leading-relaxed mt-4 bg-white border border-ink/10 rounded-2xl p-6"
                data-testid="refund-cancellation-policy"
              >
                All consulting sessions require 48-hour advance notice for cancellation or
                rescheduling. Sessions cancelled within 48 hours are non-refundable. Ongoing
                management programs may be cancelled with 30 days written notice. Trust setup
                fees are non-refundable once document preparation has begun.
              </p>
            </Reveal>
          </div>

          <Reveal>
            <div className="mt-20 bg-ink rounded-2xl p-8 sm:p-10 text-center">
              <p className="font-display text-xl sm:text-2xl text-cream leading-snug">
                Ready to get started? Contact us today.
              </p>
              <Link
                to="/contact"
                data-testid="financial-consulting-footer-cta"
                className="group inline-flex items-center gap-2 bg-gold text-ink font-bold tracking-[0.08em] uppercase text-sm px-7 py-4 rounded-full hover:bg-goldlight transition-colors duration-300 mt-6"
              >
                Contact Us
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-cream/65 text-sm mt-7">
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  data-testid="financial-consulting-support-email"
                  className="inline-flex items-center gap-2 hover:text-gold transition-colors"
                >
                  <Mail className="w-4 h-4" /> {SUPPORT_EMAIL}
                </a>
                <a
                  href={`tel:${SUPPORT_PHONE.replace(/[^\d+]/g, "")}`}
                  data-testid="financial-consulting-support-phone"
                  className="inline-flex items-center gap-2 hover:text-gold transition-colors"
                >
                  <Phone className="w-4 h-4" /> {SUPPORT_PHONE}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </article>

      <ContactFooter />
    </div>
  );
};

export default FinancialConsulting;
